import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { formatPrice, formatDate, getStatusColor, getStatusText } from '../lib/utils';
import { ShoppingBag, User, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 bg-[#09090b] text-zinc-50 relative">
      <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
      <div className="max-w-4xl mx-auto px-4 relative z-10">
        
        {/* Profile Header */}
        <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-8 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <User className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 mb-1 px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5">
              <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-mono text-[9px] text-emerald-400 font-semibold tracking-wider uppercase">Verified Account</span>
            </div>
            <h1 className="font-bold text-lg text-white">{user.name}</h1>
            <p className="text-zinc-400 text-xs font-mono">{user.email}</p>
            {user.role !== 'user' && (
              <span className="inline-block mt-1.5 px-2 py-0.5 text-[10px] rounded font-mono uppercase tracking-wider border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                {user.role}
              </span>
            )}
          </div>
        </div>

        {/* Orders */}
        <div className="bg-zinc-900/30 rounded-xl border border-zinc-850 overflow-hidden">
          <div className="p-5 border-b border-zinc-850 bg-zinc-950/40 flex items-center gap-3">
            <ShoppingBag className="w-4 h-4 text-emerald-400" />
            <h2 className="font-bold text-sm text-white uppercase tracking-wider">
              Transaction History
            </h2>
            <span className="ml-auto font-mono text-[10px] text-zinc-555">
              {loading ? '...' : `${orders.length} RECORDS`}
            </span>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
              <p className="text-xs text-zinc-500 mt-3 font-mono">Loading transaction records...</p>
            </div>
          ) : orders.length > 0 ? (
            <div className="divide-y divide-zinc-850">
              {orders.map((order) => (
                <div key={order.id} className="p-4 hover:bg-zinc-900/30 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-white text-xs">{order.product_name}</p>
                      <p className="text-[10px] text-zinc-400 font-mono">{order.denomination_name}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] rounded font-mono uppercase tracking-wider ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-mono">
                    <div className="flex items-center gap-4 text-zinc-500">
                      <span>{order.order_number}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(order.created_at)}
                      </span>
                    </div>
                    <span className="font-bold text-white">
                      {formatPrice(order.price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center">
              <ShoppingBag className="w-10 h-10 text-zinc-650 mx-auto mb-4 opacity-60" />
              <p className="text-white font-semibold text-sm mb-1">No Orders Found</p>
              <p className="text-zinc-400 text-xs mb-6">
                You haven't initiated any digital transactions yet.
              </p>
              <Button onClick={() => navigate('/')}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs px-5 py-2.5 rounded-lg">
                Start Transacting
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
